import { useEffect, useState } from "react";

/**
 * Public landing page shown to unauthenticated visitors. Lives in the same
 * SPA bundle as the app (no nginx changes) — App.tsx routes anonymous
 * sessions here instead of straight to the Login form, and the "Sign in"
 * CTA flips view → "login".
 *
 * Hand-rolled hero / features / how-it-works / templates preview +
 * animated terminal mockup as the "demo video" placeholder. Honest
 * copy, no inflated claims (the broker really is hybrid Moonshot+
 * DeepSeek + the cost numbers come from the broker's COST_RATES).
 */

interface LandingProps {
  onSignIn: () => void;
}

interface TemplatePreview {
  id: string;
  title: string;
  tagline: string;
  glyph: string;
  category: string;
  costBand: string;
}

export function Landing({ onSignIn }: LandingProps): JSX.Element {
  const [templates, setTemplates] = useState<TemplatePreview[]>([]);

  // Best-effort fetch — endpoint requires auth so for visitors this
  // fails silently and we render a baked-in subset. (Stops the
  // landing from being broken if /api/templates ever changes shape.)
  useEffect(() => {
    fetch("/api/templates", { credentials: "include" })
      .then((r) => r.json())
      .then((j) => { if (j?.ok && Array.isArray(j.templates)) setTemplates(j.templates.slice(0, 6)); })
      .catch(() => {});
  }, []);

  const fallbackTemplates: TemplatePreview[] = [
    { id: "build-landing",     title: "Landing page",       tagline: "Hero + features + pricing, mobile-ready.",         glyph: "🛬", category: "build",  costBand: "med" },
    { id: "build-rest-api",    title: "REST API",           tagline: "Express + SQLite, CRUD + auth + tests.",            glyph: "🔌", category: "build",  costBand: "high" },
    { id: "audit-security",    title: "Security audit",     tagline: "Walk codebase, file findings, fix critical.",        glyph: "🛡️", category: "audit",  costBand: "high" },
    { id: "audit-tests",       title: "Add test coverage",  tagline: "Find untested modules, write Vitest, raise to 70%+", glyph: "✅", category: "audit",  costBand: "high" },
    { id: "research-readme",   title: "Generate docs",      tagline: "Read code, write real README + API docs.",            glyph: "📚", category: "research", costBand: "med" },
    { id: "ops-fix-tests",     title: "Fix failing tests",  tagline: "Run suite, diagnose root cause, iterate green.",      glyph: "🩹", category: "ops",    costBand: "med" },
  ];
  const showTemplates = templates.length > 0 ? templates : fallbackTemplates;

  return (
    <div className="landing">
      <header className="landing-nav">
        <div className="landing-nav-inner">
          <strong className="landing-brand">DrChief <span className="muted">v2</span></strong>
          <button className="landing-cta" onClick={onSignIn}>Sign in →</button>
        </div>
      </header>

      <section className="landing-hero">
        <div className="landing-hero-copy">
          <h1>Autonomous workflow IDE for personal projects.</h1>
          <p className="landing-lede">
            Tell DrChief what to build. <strong>Workflow plans the DAG</strong>, <strong>Manager dispatches Executors</strong>,
            <strong> Reviewer gates output</strong>, and the system <strong>auto-recovers on stalls</strong> — without
            human supervision. Output drops directly into git when the run finishes.
          </p>
          <div className="landing-hero-cta-row">
            <button className="landing-cta landing-cta-primary" onClick={onSignIn}>Sign in &amp; start</button>
            <a className="landing-cta landing-cta-ghost" href="#templates">See what it can do</a>
          </div>
          <div className="landing-hero-meta">
            <span className="landing-pill"><span className="dot dot-good" /> Hybrid Kimi K2.6 + DeepSeek V4</span>
            <span className="landing-pill"><span className="dot dot-good" /> Live cost meter</span>
            <span className="landing-pill"><span className="dot dot-good" /> No vendor lock-in</span>
          </div>
        </div>

        <div className="landing-demo">
          <DemoTerminal />
        </div>
      </section>

      <section className="landing-section">
        <h2>How it works</h2>
        <div className="landing-steps">
          <div className="landing-step">
            <div className="landing-step-num">1</div>
            <h3>Pick a template or describe a goal</h3>
            <p className="muted">10 quickstart templates cover landing pages, REST APIs, security audits, type cleanup, doc generation, fix-failing-tests loops, and more. Or write your own — the Workflow agent breaks it into a DAG.</p>
          </div>
          <div className="landing-step">
            <div className="landing-step-num">2</div>
            <h3>Agents execute in parallel, gated by review</h3>
            <p className="muted">Manager dispatches Coding / Frontend / Test / Content / Build / Security executors as child Node processes. File-glob conflicts go serial; non-overlapping work runs in parallel. Reviewer rejects bad output with confidence scores.</p>
          </div>
          <div className="landing-step">
            <div className="landing-step-num">3</div>
            <h3>Output ships to git, you review at your leisure</h3>
            <p className="muted">When the session reaches done/warned, optional auto-commit pipeline runs <code>git add -A &amp;&amp; git commit</code> (and <code>git push</code> if you opted in). Or download the workspace as <code>.tar.gz</code>. Or browse files directly in the UI.</p>
          </div>
        </div>
      </section>

      <section className="landing-section landing-features">
        <h2>What you actually get</h2>
        <div className="landing-feature-grid">
          <Feature glyph="🧠" title="Multi-agent topology" body="Workflow / Manager / Memory / Reviewer + 10 Executor types (Coding / Frontend / Graphics / Content / Test / Security / Build / Integration / TestEnv / Review). Sub-Managers spawn for overflow tracks. Each executor is a child Node process with its own LLM context window." />
          <Feature glyph="🛡️" title="Auto-recovery" body="Per-node activity watchdog (5 min default). Stalled agents get killed and re-spawned with reasoning-tier model + fresh framing. Rate limits + auth/model-access errors trigger cross-vendor failover (Kimi <-> DeepSeek). No manual intervention." />
          <Feature glyph="📁" title="Live output preview" body="Browse the session's working folder in the UI as agents write to it. View source files, render images, copy/edit/diff. Or download the whole tree as a tar.gz. Or auto-commit + push to your git remote." />
          <Feature glyph="📋" title="Real Plan/Act mode" body="Plan mode flips every action node to requiresApproval=true so Claude/Kimi plans the work then waits for you to approve each node. Act mode runs through. Toggle per session." />
          <Feature glyph="🔐" title="Three permission modes" body="Safe (per-agent allowlist + path containment + shell binary allowlist), Extended (placeholder for git push / curl), Bypass (TURBO full access — Linux DAC + systemd hardening still gate). Pick per session." />
          <Feature glyph="💸" title="Cost meter live" body="Per-provider account: in-flight, calls, tokens, USD. Per-agent-type: which roles dominate spend. Snapshots every 4 seconds. Hybrid Kimi + DeepSeek keeps code-heavy work on the cheaper provider and coordination on Kimi." />
        </div>
      </section>

      <section className="landing-section" id="templates">
        <h2>Quickstart templates</h2>
        <p className="muted">Click "Sign in" to load any of these into a session with one click.</p>
        <div className="landing-template-grid">
          {showTemplates.map((t) => (
            <div className="landing-template-card" key={t.id}>
              <div className="landing-template-head">
                <span className="landing-template-glyph" aria-hidden="true">{t.glyph}</span>
                <strong>{t.title}</strong>
                <span className={`landing-template-cost band-${t.costBand}`}>{t.costBand}</span>
              </div>
              <p className="muted">{t.tagline}</p>
              <span className={`landing-template-cat`}>{t.category}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="landing-section landing-tech">
        <h2>Stack</h2>
        <ul className="landing-tech-list">
          <li><strong>Backend:</strong> Node 22 + TypeScript ESM + ws + better-sqlite3. Pure stdlib HTTP server. ChildAgentBridge spawns one LLM-driven child per executor over JSON-RPC 2.0 stdio.</li>
          <li><strong>UI:</strong> React 19 + Vite 8 + reactflow (xyflow DAG). No SSR — single-page, served as a static bundle by nginx.</li>
          <li><strong>LLMs:</strong> Hybrid Moonshot Kimi K2.6/K2.5 + DeepSeek V4 Flash/Pro. Cross-vendor failover, no Claude/OpenAI/Gemini fallback.</li>
          <li><strong>Cost:</strong> Live token/cost meter per provider and per agent type.</li>
        </ul>
      </section>

      <section className="landing-section landing-final">
        <h2>Ready?</h2>
        <p className="muted">Personal-use deployment. Sign in to start a session. (Account creation is locked to the seeded admin — single-user for now.)</p>
        <button className="landing-cta landing-cta-primary landing-cta-large" onClick={onSignIn}>Sign in to neurovhlabs.com</button>
      </section>

      <footer className="landing-footer">
        <span className="muted">DrChief — autonomous multi-agent workflow IDE.</span>
        <span className="muted">Personal use. <a href="https://github.com/vh0202/DrChief" target="_blank" rel="noopener noreferrer">github.com/vh0202/DrChief</a></span>
      </footer>
    </div>
  );
}

function Feature({ glyph, title, body }: { glyph: string; title: string; body: string }): JSX.Element {
  return (
    <div className="landing-feature">
      <span className="landing-feature-glyph" aria-hidden="true">{glyph}</span>
      <h3>{title}</h3>
      <p className="muted">{body}</p>
    </div>
  );
}

/**
 * Animated terminal mock — "demo video" without a real video. Cycles
 * through a representative session log every 9 seconds. Pure CSS +
 * setInterval; no recordings, no third-party assets.
 */
function DemoTerminal(): JSX.Element {
  const [step, setStep] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setStep((s) => (s + 1) % DEMO_LINES.length), 1100);
    return () => clearInterval(t);
  }, []);
  return (
    <div className="landing-terminal">
      <div className="landing-terminal-bar">
        <span className="landing-terminal-dot landing-terminal-dot-r" />
        <span className="landing-terminal-dot landing-terminal-dot-y" />
        <span className="landing-terminal-dot landing-terminal-dot-g" />
        <span className="landing-terminal-title">drchief — sess-build-rest-api</span>
      </div>
      <div className="landing-terminal-body">
        {DEMO_LINES.slice(0, step + 1).map((line, i) => (
          <div className={`landing-terminal-line ${line.cls ?? ""}`} key={i}>
            <span className="landing-terminal-time">{line.time}</span>
            <span className="landing-terminal-text">{line.text}</span>
          </div>
        ))}
        {step < DEMO_LINES.length - 1 && <span className="landing-terminal-cursor">▍</span>}
      </div>
    </div>
  );
}

const DEMO_LINES: Array<{ time: string; text: string; cls?: string }> = [
  { time: "00:00", text: "$ POST /api/sessions  request='Build REST API'",                              cls: "" },
  { time: "00:01", text: "▶ workflow agent: planning DAG …",                                             cls: "" },
  { time: "00:08", text: "✓ plan_ready  8 nodes  (coding 3, test 2, build 1, review 1, content 1)",     cls: "good" },
  { time: "00:09", text: "▶ manager: dispatching scaffold + db-schema in parallel",                     cls: "" },
  { time: "00:24", text: "✓ scaffold      kimi-k2 → 4 files written",                                    cls: "good" },
  { time: "00:25", text: "✓ db-schema     deepseek-v4-pro → schema.sql, models/*.ts",                    cls: "good" },
  { time: "00:31", text: "▶ auth-routes   deepseek-v4-pro → 6 turns",                                    cls: "" },
  { time: "00:46", text: "⚠ reviewer: missing input validation → retry @ reasoning tier",               cls: "warn" },
  { time: "01:02", text: "✓ auth-routes   deepseek-v4-pro → 11 turns, zod schemas added",               cls: "good" },
  { time: "01:03", text: "▶ test-suite    moonshot kimi-k2.6 → vitest 23/23 passing",                   cls: "" },
  { time: "01:18", text: "✓ session_done  status=done, $0.047 spent",                                   cls: "good" },
  { time: "01:18", text: "✓ autoCommit    git push origin main → 7c5a3f2",                              cls: "good" },
];
